import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AgentMobilePortal from './AgentMobilePortal';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Shield, 
  AlertTriangle, 
  Camera, 
  Activity,
  TrendingUp,
  Clock,
  MapPin,
  Eye,
  Lock,
  CreditCard
} from 'lucide-react';
import { sitesAPI, agentsAPI, camerasAPI, alarmsAPI } from '../services/api';


const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Vérifier si l'utilisateur est un agent
  const userRole = user?.role || 'user';
  const isAgent = userRole === 'user' || userRole === 'agent';
  
  // Si c'est un agent, afficher le portail mobile
  if (isAgent) {
    return <AgentMobilePortal />;
  }
  
  // Dashboard Admin continue ici...
  const [stats, setStats] = useState({
    totalSites: 0,
    activeCameras: 0,
    onlineAgents: 0,
    activeAlerts: 0
  });
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [activeSites, setActiveSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [systemStatus] = useState([
    { name: 'Caméras', status: 'Actif', uptime: '99.9%', description: 'Système opérationnel' },
    { name: 'Alarmes', status: 'Actif', uptime: '99.5%', description: 'Système opérationnel' },
    { name: 'Accès', status: 'Actif', uptime: '98.8%', description: 'Système opérationnel' }
  ]);

  // Load real data from APIs
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load all data in parallel
      const [sitesRes, agentsRes, camerasRes, alarmsRes] = await Promise.all([
        sitesAPI.getAll(),
        agentsAPI.getAll(),
        camerasAPI.getAll(),
        alarmsAPI.getAll()
      ]);

      // Calculate stats
      setStats({
        totalSites: sitesRes.data.length,
        activeCameras: camerasRes.data.filter(c => c.status === 'En ligne' || c.status === 'online').length,
        onlineAgents: agentsRes.data.filter(a => a.status === 'active').length,
        activeAlerts: alarmsRes.data.filter(a => a.status === 'Active' || a.status === 'active').length
      });

      // Set recent alerts (last 5)
      setRecentAlerts(alarmsRes.data.slice(0, 5));

      // Set active sites (first 5)
      setActiveSites(sitesRes.data.slice(0, 5));


    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'critique': return 'bg-red-500';
      case 'important': return 'bg-orange-500';
      case 'moyen': return 'bg-yellow-500';
      case 'faible': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status) => {
    return status === 'Actif' ? 'bg-green-500' : 'bg-red-500';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Tableau de bord</h2>
          <p className="text-gray-600">Vue d'ensemble de votre système de sécurité</p>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
          <span className="text-sm text-gray-600">Système opérationnel</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Sites surveillés</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalSites}</div>
            <p className="text-xs text-muted-foreground">
              <span className="text-green-600">Actifs</span>
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Caméras actives</CardTitle>
            <Camera className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeCameras}</div>
            <p className="text-xs text-muted-foreground">
              Opérationnelles
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Alertes actives</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.activeAlerts}</div>
            <p className="text-xs text-muted-foreground">
              En cours
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Agents actifs</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.onlineAgents}</div>
            <p className="text-xs text-muted-foreground">
              En service
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5" />
              <span>Alertes récentes</span>
            </CardTitle>
            <CardDescription>
              Dernières alertes de sécurité détectées
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentAlerts.map((alert) => (
                <div key={alert.id} className="flex items-start space-x-3 p-3 border rounded-lg">
                  <div className={`w-2 h-2 rounded-full mt-2 ${getAlertColor(alert.severity)}`}></div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-medium text-sm">{alert.title}</h4>
                      <Badge variant={alert.severity === 'critique' ? 'destructive' : 'secondary'}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                    <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center space-x-1">
                        <MapPin className="w-3 h-3" />
                        <span>{alert.location}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Clock className="w-3 h-3" />
                        <span>{alert.time}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Active Sites */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Activity className="w-5 h-5" />
              <span>Sites actifs</span>
            </CardTitle>
            <CardDescription>
              État en temps réel de vos sites surveillés
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {activeSites.map((site) => (
                <div key={site.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(site.status)}`}></div>
                    <div>
                      <h4 className="font-medium text-sm">{site.name}</h4>
                      <p className="text-xs text-gray-600">{site.address}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-2 text-sm">
                      <Eye className="w-4 h-4" />
                      <span>{site.cameras}</span>
                      <Lock className="w-4 h-4 ml-2" />
                      <span>{site.accessPoints}</span>
                    </div>
                    <p className="text-xs text-gray-500">{site.lastUpdate}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5" />
            <span>État du système</span>
          </CardTitle>
          <CardDescription>
            Performance et statut des composants principaux
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {systemStatus.map((component) => (
              <div key={component.name} className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <div className={`w-4 h-4 rounded-full ${getStatusColor(component.status)} mr-2`}></div>
                  <span className="font-medium">{component.name}</span>
                </div>
                <div className="text-2xl font-bold mb-1">{component.uptime}</div>
                <div className="text-sm text-gray-600">{component.description}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;